import { create } from 'zustand';

/**
 * Configuration for the confirmation dialog
 */
export interface ConfirmConfig {
  /**
   * Title of the confirmation dialog
   */
  title: string;
  /**
   * Description of the confirmation dialog
   */
  description: string | React.ReactNode;
  /**
   * Text for the confirm button
   */
  confirmText?: string | React.ReactNode;
  /**
   * Text for the cancel button
   */
  cancelText?: string | React.ReactNode;
  /**
   * Variant of the confirmation dialog
   */
  variant?: 'default' | 'destructive';
}

/**
 * The ConfirmStore manages the state of confirmation dialogs
 */
interface ConfirmStore {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Configuration for the current dialog
   */
  config: ConfirmConfig | null;
  /**
   * Promise resolve function for the current confirmation
   */
  resolve: ((value: boolean) => void) | null;
  /**
   * Opens a confirmation dialog with the given configuration
   */
  confirm: (config: ConfirmConfig) => Promise<boolean>;
  /**
   * Handles the confirm action (Yes button)
   */
  handleConfirm: () => void;
  /**
   * Handles the cancel action (No button)
   */
  handleCancel: () => void;
  /**
   * Closes the dialog without resolving
   */
  close: () => void;
}

/**
 * Zustand store for managing confirmation dialogs
 */
export const useConfirmStore = create<ConfirmStore>((set, get) => ({
  /**
   * Whether the dialog is open
   */
  isOpen: false,
  /**
   * Configuration for the current dialog
   */
  config: null,
  /**
   * Promise resolve function for the current confirmation
   */
  resolve: null,

  /**
   * Opens a confirmation dialog with the given configuration
   */
  confirm: (config: ConfirmConfig) => {
    return new Promise<boolean>((resolve) => {
      set({
        isOpen: true,
        config: {
          confirmText: 'Yes',
          cancelText: 'No',
          variant: 'default',
          ...config,
        },
        resolve,
      });
    });
  },

  /**
   * Handles the confirm action (Yes button)
   */
  handleConfirm: () => {
    const { resolve } = get();
    set({ isOpen: false, config: null, resolve: null });
    resolve?.(true);
  },

  /**
   * Handles the cancel action (No button)
   */
  handleCancel: () => {
    const { resolve } = get();
    set({ isOpen: false, config: null, resolve: null });
    resolve?.(false);
  },

  /**
   * Closes the dialog without resolving
   */
  close: () => {
    const { resolve } = get();
    set({ isOpen: false, config: null, resolve: null });
    resolve?.(false);
  },
})); 
